import type { CustomerOverview, CustomerProfile, Row } from '@contract';
import type { ReactNode } from 'react';
import { ActionsTab } from '@/components/customer-actions';
import type { DetailsReveal } from '@/components/customer-details';
import { AuditTab } from '@/components/customer-overview';
import {
  ActivityTab,
  DevicesTab,
  IssuesTab,
  MessagesTab,
  ProfileTab,
  SavedTab,
  StatementTab,
  type TransactionFilters,
  TransactionsTab,
  VerificationTab,
  WalletsTab,
} from '@/components/customer-tabs';
import { Money } from '@/components/primitives/money';
import { StatusPill } from '@/components/primitives/status-pill';
import { OpenRecord } from '@/components/queue/queue-parts';
import { SlaChip } from '@/components/queue/sla-chip';
import { RegistrationCard } from '@/components/registration-card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFrame,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime, relativeTime } from '@/lib/format';
import { homeCurrency } from '@/lib/market';

/**
 * The 13 old tabs, grouped into the six sections of §9.4. The sheet shows
 * them as tabs (`stab`); the full page shows them as a section rail. An old
 * link such as `?tab=wallets` lands on the section that now holds it.
 */
export const CUSTOMER_SECTIONS = [
  { key: 'overview', label: 'Overview' },
  { key: 'money', label: 'Money' },
  { key: 'identity', label: 'Identity and security' },
  { key: 'support', label: 'Support' },
  { key: 'actions', label: 'Actions' },
  { key: 'audit', label: 'Audit' },
] as const;

export type CustomerSection = (typeof CUSTOMER_SECTIONS)[number]['key'];

const LEGACY_TABS: Record<string, CustomerSection> = {
  profile: 'identity',
  verification: 'identity',
  activity: 'identity',
  devices: 'identity',
  wallets: 'money',
  transactions: 'money',
  statement: 'money',
  saved: 'money',
  messages: 'support',
  issues: 'support',
};

/** A section key from the URL, including the old tab names. */
export function toSection(value: unknown): CustomerSection | undefined {
  if (typeof value !== 'string') return undefined;
  if (CUSTOMER_SECTIONS.some((section) => section.key === value)) {
    return value as CustomerSection;
  }
  return LEGACY_TABS[value];
}

export interface SectionProps {
  customerId: string;
  profile: CustomerProfile;
  overview: CustomerOverview | undefined;
  reveal: DetailsReveal;
  filters: TransactionFilters;
  onFilters: (filters: TransactionFilters) => void;
  /** Moves to another section (the sheet's tab, or the page's rail). */
  onSection: (section: CustomerSection) => void;
}

/** The body of one section. */
export function CustomerSectionBody({
  section,
  ...props
}: Readonly<SectionProps & { section: CustomerSection }>) {
  switch (section) {
    case 'money':
      return <MoneySection {...props} />;
    case 'identity':
      return <IdentitySection {...props} />;
    case 'support':
      return <SupportSection customerId={props.customerId} />;
    case 'actions':
      return (
        <ActionsTab customerId={props.customerId} profile={props.profile} />
      );
    case 'audit':
      return <AuditTab customerId={props.customerId} />;
    default:
      return <OverviewSection {...props} />;
  }
}

function Block({
  title,
  action,
  children,
}: Readonly<{ title: string; action?: ReactNode; children: ReactNode }>) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Overview

function OverviewSection({
  profile,
  overview,
  onSection,
}: Readonly<SectionProps>) {
  const wallets = overview?.wallets ?? [];
  const payments = overview?.transactions.recent ?? [];
  const reports = overview?.issues.recent ?? [];
  return (
    <>
      {profile.registration ? (
        <RegistrationCard status={profile.registration} />
      ) : null}

      <Block title="Wallets">
        <TableFrame>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Wallet</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Posted</TableHead>
                <TableHead className="text-right">Held</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {wallets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">
                    No wallets yet.
                  </TableCell>
                </TableRow>
              ) : null}
              {wallets.map((wallet) => (
                <TableRow key={wallet.id}>
                  <TableCell label="Wallet">
                    <span className="font-medium">{wallet.name}</span>{' '}
                    <span className="text-muted-foreground">{wallet.kind}</span>
                  </TableCell>
                  <TableCell label="Status">
                    <StatusPill status={wallet.status} />
                  </TableCell>
                  <TableCell label="Posted" className="text-right">
                    <Money
                      minor={wallet.posted_minor}
                      currency={wallet.currency}
                    />
                  </TableCell>
                  <TableCell label="Held" className="text-right">
                    <Money
                      minor={wallet.held_minor}
                      currency={wallet.currency}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableFrame>
      </Block>

      <Block
        title="Recent payments"
        action={
          <Button size="xs" variant="ghost" onClick={() => onSection('money')}>
            All payments in Money
          </Button>
        }
      >
        <TableFrame>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>What</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">
                    No payments yet.
                  </TableCell>
                </TableRow>
              ) : null}
              {payments.slice(0, 10).map((payment) => (
                <PaymentRow key={String(payment.id)} payment={payment} />
              ))}
            </TableBody>
          </Table>
        </TableFrame>
      </Block>

      <Block title="Open work">
        {reports.length === 0 && (overview?.cases.open ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            No reports or cases are open.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {reports.map((report) => (
              <li
                key={String(report.id)}
                className="flex items-center gap-3 px-3 py-2"
              >
                <OpenRecord
                  sheetRef={{ type: 'issue', id: String(report.id) }}
                  label={`Open report ${String(report.id).slice(0, 8)}`}
                  className="flex-1 truncate text-sm font-medium hover:underline"
                >
                  {productName(report.product)} report
                </OpenRecord>
                <StatusPill status={String(report.status)} />
                {typeof report.due_at === 'string' &&
                typeof report.status === 'string' &&
                report.status !== 'resolved' &&
                report.status !== 'closed' ? (
                  <SlaChip at={report.due_at} />
                ) : null}
              </li>
            ))}
            {(overview?.cases.open ?? 0) > 0 ? (
              <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span>
                  {overview?.cases.open} open{' '}
                  {overview?.cases.open === 1 ? 'case' : 'cases'}
                </span>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => onSection('support')}
                >
                  See in Support
                </Button>
              </li>
            ) : null}
          </ul>
        )}
      </Block>

      <Block title="Identity summary">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <Fact label="ID check" value={idCheckText(profile)} />
          <Fact
            label="Devices"
            value={String(overview?.devices.count ?? '—')}
          />
          <Fact
            label="Last activity"
            value={
              overview?.last_activity_at
                ? formatDateTime(overview.last_activity_at)
                : 'None yet'
            }
          />
        </dl>
      </Block>
    </>
  );
}

function idCheckText(profile: CustomerProfile): string {
  if (!profile.verified) return 'Not verified';
  const when = profile.verified_at ? relativeTime(profile.verified_at) : '';
  return `Verified ${when}`.trim();
}

function PaymentRow({ payment }: Readonly<{ payment: Row }>) {
  const id = String(payment.id);
  const currency =
    typeof payment.currency === 'string' ? payment.currency : homeCurrency();
  return (
    <TableRow>
      <TableCell label="When" className="text-xs text-muted-foreground">
        {relativeTime(String(payment.created_at))}
      </TableCell>
      <TableCell label="What">
        <OpenRecord
          sheetRef={{ type: 'tx', id }}
          label={`Open ${String(payment.kind).replaceAll('_', ' ')} payment`}
          className="font-medium hover:underline"
        >
          {String(payment.kind).replaceAll('_', ' ')}
        </OpenRecord>
      </TableCell>
      <TableCell label="Status">
        <StatusPill status={String(payment.status)} />
      </TableCell>
      <TableCell label="Amount" className="text-right">
        <Money minor={Number(payment.amount_minor)} currency={currency} />
      </TableCell>
    </TableRow>
  );
}

const PRODUCT_NAME: Record<string, string> = {
  send: 'Send',
  add_money: 'Top-up',
  momo_payout: 'Mobile money',
  bill: 'Bill payment',
};

function productName(product: unknown): string {
  return typeof product === 'string'
    ? (PRODUCT_NAME[product] ?? product.replaceAll('_', ' '))
    : 'Issue';
}

function Fact({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Money, Identity, Support (the old tabs, grouped)

function MoneySection({
  customerId,
  filters,
  onFilters,
}: Readonly<SectionProps>) {
  return (
    <>
      <Block title="Wallets">
        <WalletsTab customerId={customerId} />
      </Block>
      <Block title="Payments">
        <TransactionsTab
          key={JSON.stringify(filters)}
          customerId={customerId}
          filters={filters}
          onFilters={onFilters}
        />
      </Block>
      <Block title="Statement">
        <StatementTab customerId={customerId} />
      </Block>
      <Block title="Saved recipients and funding numbers">
        <SavedTab customerId={customerId} />
      </Block>
    </>
  );
}

function IdentitySection({
  customerId,
  profile,
  reveal,
}: Readonly<SectionProps>) {
  return (
    <>
      <Block title="Verification">
        <VerificationTab customerId={customerId} />
      </Block>
      <Block title="Details, PIN and limits">
        <ProfileTab profile={profile} reveal={reveal} />
      </Block>
      <Block title="Devices">
        <DevicesTab customerId={customerId} />
      </Block>
      <Block title="Activity">
        <ActivityTab customerId={customerId} />
      </Block>
    </>
  );
}

function SupportSection({ customerId }: Readonly<{ customerId: string }>) {
  return (
    <>
      <Block title="Reports and cases">
        <IssuesTab customerId={customerId} />
      </Block>
      <Block title="Messages">
        <MessagesTab customerId={customerId} />
      </Block>
    </>
  );
}
